<template>
  <article class="faq-column">
    <h1>Foire Aux Questions (FAQs)</h1>
    <p>
      Répondre à certaines des questions les plus fréquemment posées concernant les véhicules électriques (VE) et la
      façon de recharger sur le réseau Electrify Canada.
    </p>

    <accordion>
      <!-- Electrify Canada -->
      <accordion-item
        :headerBorder="true"
        title="Electrify Canada"
        :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 0 } }, true)"
      >
        <accordion>
          <accordion-item
            title="Qu’est-ce qu’Electrify Canada?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 0, loadInner: 0 } })"
          >
            <p>
              Electrify Canada construit une infrastructure transformatrice de recharge ultrarapide en courant continu
              (CC) de véhicules électriques (VE) pour donner aux Canadiens la vitesse et la fiabilité dont ils ont
              besoin pour passer sans crainte à l’électrique.
            </p>
          </accordion-item>
          <accordion-item
            title="Est-ce que seuls les véhicules VW, Audi et Porsche peuvent utiliser electrify Canada?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 0, loadInner: 1 } })"
          >
            <p>Tout VE doté d’une entrée de recharge compatible CCS ou CHAdeMO peut utiliser Electrify Canada.</p>
          </accordion-item>
        </accordion>
      </accordion-item>
      <!-- Public Charging -->
      <accordion-item
        :headerBorder="true"
        title="Recharge Publique"
        :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 1 } }, true)"
      >
        <accordion>
          <accordion-item
            title="Comment puis-je trouver une station de recharge Electrify Canada?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 1, loadInner: 0 } })"
          >
            <p>
              Pour trouver une station de recharge Electrify Canada près de vous, effectuez une recherche sur la carte
              en ligne <router-link class="link" to="/fr/trouver-une-borne/">ici.</router-link> Vous pouvez également
              télécharger
              <router-link :to="{ name: 'mobile-app-fr' }" class="link">l’application Electrify Canada</router-link>
              pour localiser une station ouverte, suivre les stations qui seront bientôt ouvertes et recevoir des
              notifications lorsqu’elles le seront.
            </p>
          </accordion-item>
          <accordion-item
            title="Pourquoi la borne de recharge affiche-t-elle un coût lorsque j’utilise mon offre Premium gratuite pour la recharge?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 1, loadInner: 1 } })"
          >
            <p>
              La borne de recharge indique le prix initial de la recharge avant l’application des promotions et des
              taxes. À la fin de votre session, vous verrez votre prix lorsque l’offre Premium sera appliquée.
            </p>
          </accordion-item>
          <accordion-item
            title="Pourquoi certaines bornes de recharge disent-elles « temporairement réduit »?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 1, loadInner: 2 } })"
          >
            <p>
              Souvent, lorsque vous voyez une borne de recharge qui fournit une puissance réduite, cela signifie que
              notre équipe d’entretien a temporairement réduit la puissance de la borne pour y faire des améliorations
              et des mises à jour. Bien que ces bornes de recharge fonctionnent toujours, elles fonctionnent à des
              niveaux de puissance réduits. Nous comprenons les inconvénients d’une vitesse de recharge plus lente et
              nous travaillons toujours à améliorer votre expérience afin d’accélérer les recharges.
            </p>
            <p>
              Veuillez noter que les sites ne sont pas tous touchés. Pour voir d’avance le niveau de puissance des
              bornes à une station, utilisez l’application mobile Electrify Canada.
            </p>
            <p>Merci pour votre patience et votre soutien.</p>
          </accordion-item>
          <accordion-item
            title="Que dois-je faire si une erreur de recharge se produit?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 1, loadInner: 3 } })"
          >
            <p>
              Dans bien des cas, les erreurs de recharge sont temporaires et peuvent être résolues en arrêtant et en
              redémarrant votre session. Voici ce qu’il faut faire :
            </p>
            <ol>
              <li>
                Mettez fin à votre session de recharge manuellement en appuyant sur « Arrêter » dans l’application
                mobile ou sur la borne de recharge.
              </li>
              <li>
                Débranchez le câble de recharge de votre véhicule. Si nécessaire, déverrouillez votre véhicule
                électrique avant de débrancher le câble de recharge.
              </li>
              <li>Rebranchez le câble de recharge et commencez une nouvelle session de recharge.</li>
              <li>
                Si l’erreur persiste, nous vous suggérons de vous déplacer vers une autre borne de recharge et de
                signaler le problème en composant le numéro du centre d’assistance à la clientèle situé au-dessus de
                l’écran de la borne de recharge.
              </li>
              <li>
                Pour obtenir des instructions plus détaillées sur la façon de recharger, consultez
                <router-link class="link" :to="{ name: 'charging-your-ev-fr' }">Comment recharger votre VE</router-link>
              </li>
            </ol>
          </accordion-item>
          <accordion-item
            title="Ai-je besoin d’une adhésion Passe ou Passe+ Electrify Canada pour recharger?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 1, loadInner: 4 } })"
          >
            <p>
              Non, il n’est pas nécessaire d’adhérer à un forfait d’Electrify Canada, mais l’adhésion comporte des
              avantages. Par exemple, les membres Passe+ économisent 20 % chaque fois qu’ils rechargent. Pour en savoir
              plus sur les avantages d’adhérer à un forfait Passe ou Passe+,
              <router-link class="link" :to="{ name: 'mobile-app-fr' }">cliquez ici</router-link>.
            </p>
            <p>
              Si vous avez besoin d’un soutien supplémentaire ou si vous devez commencer une session de recharge par
              téléphone, un numéro sans frais est indiqué sur chaque borne de recharge.
            </p>
          </accordion-item>
          <accordion-item
            title="Quels modes de paiement sont acceptés?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 1, loadInner: 5 } })"
          >
            <p>
              Nous acceptons le paiement par carte de crédit ou de débit directement à la borne de recharge. Les cartes
              acceptées sont Visa, Mastercard, American Express ou Discover. Nous acceptons également le paiement par
              l’entremise de l’application mobile Electrify Canada, des applications d’autres fabricants de véhicules,
              d’Apple Pay et de Google Wallet.
            </p>
          </accordion-item>
          <accordion-item
            title="Puis-je utiliser une carte d’adhésion ou l’application d’un autre réseau de recharge aux bornes d’Electrify Canada?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 1, loadInner: 6 } })"
          >
            <p>
              Aucune carte d’adhésion ou application d’un autre réseau de recharge ne peut être utilisée aux bornes
              d’Electrify Canada.
            </p>
          </accordion-item>
          <HowToStartSession
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 1, loadInner: 7 } })"
          ></HowToStartSession>
          <accordion-item
            title="Si je ne suis pas membre, comment puis-je commencer une session de recharge à une borne de recharge Electrify Canada?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 1, loadInner: 8 } })"
          >
            <p>
              En tant qu’invité (sans compte Electrify Canada), vous pouvez payer par carte de crédit. Chaque borne de
              recharge Electrify Canada est équipé d’un lecteur de carte de crédit afin de rendre la recharge facilement
              accessible à tous les utilisateurs d’un VE.
            </p>
            <p>
              Branchez d’abord le connecteur au véhicule. Suivez toutes les étapes sur l’écran tactile avant de saisir
              une carte de crédit Visa ou Mastercard pour le paiement. Si votre carte est munie d’une puce, veuillez
              l’insérer complètement dans la partie inférieure du lecteur.
            </p>
            <p>
              Une autorisation temporaire de 60 $ sera appliquée à toutes les cartes de crédit ou de débit, et elle sera
              libérée après la facturation La durée de cette retenue est déterminée par votre fournisseur de carte.
            </p>
          </accordion-item>
          <accordion-item
            title="Que signifient les étiquettes sur les bornes Electrify Canada?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 1, loadInner: 9 } })"
          >
            <p>
              Nous utilisons des étiquettes de la borne créées en collaboration avec des conducteurs comme vous! Dans le
              haut de chaque étiquette, vous trouverez des icônes de connecteur qui correspondent au port de recharge de
              votre véhicule. Dans le bas, des icônes représentant un éclair indiquent la puissance de la borne : plus
              la valeur de kilowatt (kW) est élevée, plus la puissance de recharge est élevée.
              <br />Nos stations de recharge proposent trois types de connecteurs :
            </p>
            <ul style="list-style-type: disc; margin-bottom: 10px">
              <li>
                Les étiquettes vertes indiquent les connecteurs SRC <strong>hyper rapides</strong> procurant jusqu’à 350
                kW.
              </li>
              <li>
                Les étiquettes turquoise indiquent les connecteurs SRC <strong>ultrarapides</strong> procurant jusqu’à
                150 kW.
              </li>
              <li>
                Les étiquettes bleues indiquent les connecteurs de recharge <strong>« CHAdeMO »</strong> procurant
                jusqu’à 50 kW.
              </li>
            </ul>
            <p>
              <img loading="lazy" :src="require('@/assets/images/Faq/charging-labels-fr.png')" alt="" />
            </p>
            <p>
              <strong> Conseil :</strong> Ce ne sont pas tous les véhicules électriques qui prennent en charge le niveau
              de puissance maximal d’un connecteur. Par exemple, une étiquette « hyper rapide » signifie que la borne
              offre jusqu’à 350 kW pour un VE compatible avec les connecteurs SRC. Si votre véhicule ne peut pas
              recevoir une charge maximale de 350 kW, alors la borne lui fournit automatiquement la puissance la plus
              élevée qu’il peut prendre en charge. Pour connaître les capacités de recharge de votre véhicule, consultez
              le manuel du fabricant. Si vous avez besoin d’assistance à la station, communiquez avec notre équipe de
              service à la clientèle. Vous pouvez nous joindre 24 heures sur 24, 7 jours sur 7 en composant le numéro
              sans frais <strong>1 833 231-3226</strong>.
            </p>
          </accordion-item>
          <accordion-item
            title="Si un connecteur sur une borne de recharge est déjà branché sur un véhicule, puis-je utiliser l’autre?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 1, loadInner: 10 } })"
          >
            <p>
              Un seul connecteur par borne de recharge rapide CC d’Electrify Canada peut être utilisé en même temps. Si
              l’un ou l’autre des connecteurs est en cours d’utilisation, veuillez chercher la borne de recharge
              disponible suivante sur ce site. Chacune de nos bornes de recharge est dotée de deux connecteurs adaptés
              aux véhicules avec un emplacement de port de recharge différent.
            </p>
          </accordion-item>
          <accordion-item
            title="À quelle vitesse puis-je recharger?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 1, loadInner: 11 } })"
          >
            <p>
              Chaque emplacement offre des vitesses de chargement pouvant atteindre 150 kW ou 350 kW pour les véhicules
              compatibles. En fonction d’un certain nombre de facteurs, surtout la capacité du véhicule, les vitesses de
              recharge peuvent fournir une autonomie de 4 à 30 km pour chaque minute de recharge.<sup>1</sup>
            </p>
            <p>
              <small>
                <sup>1</sup>
                Chiffres basés sur des estimations internes et susceptibles de changer. L’autonomie réelle varie en
                fonction des éléments suivants, mais sans s’y limiter : type de véhicule; habitudes de conduite et de
                recharge; météo et température; âge de la batterie et état du véhicule. Veuillez noter que la capacité
                de la batterie diminue avec le temps et l’utilisation.
              </small>
            </p>
          </accordion-item>
          <accordion-item
            title="Que sont les frais d’inactivité?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 1, loadInner: 12 } })"
          >
            <p>
              Les conducteurs qui ne débranchent pas et ne déplacent pas leur véhicule dix minutes après la fin de leur
              session de recharge seront facturés des frais d’inactivité de 0,40 $ par minute. Ces frais encouragent les
              conducteurs à déplacer leur voiture rapidement afin que d’autres puissent utiliser la borne de recharge.
              Vous pouvez être averti lorsque votre session de recharge est terminée par texto<sup>2</sup> ou le voir
              sur l’écran de la borne de recharge.
            </p>
            <p>
              <small>
                <sup>2</sup>
                Des frais de messagerie et de données peuvent s’appliquer.
              </small>
            </p>
          </accordion-item>
          <accordion-item
            title="Comment obtenir un reçu?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 1, loadInner: 13 } })"
          >
            <p>
              Si vous avez une adhésion, ouvrez une session dans l’application Electrify Canada, allez à votre compte et
              sélectionnez « Historique de recharge ». Vous pouvez filtrer votre historique par date et le partager par
              courriel. Vous pouvez aussi recevoir votre reçu par message texte si vous indiquez votre numéro de
              téléphone cellulaire au début de la session. (Des frais de messagerie et de données pourraient
              s’appliquer.) Le coût total sera également affiché sur l’écran de la borne de recharge à la fin de votre
              session.
            </p>
          </accordion-item>
          <accordion-item
            title="Comment puis-je suivre la progression de ma session de recharge?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 1, loadInner: 14 } })"
          >
            <p>
              Si vous fournissez votre numéro de mobile au début de votre session, vous pouvez recevoir des mises à jour
              par texto pour voir*:
            </p>

            <ul>
              <li>Quand la recharge du véhicule a commencé</li>
              <li>Quand la recharge du véhicule s’est ralentie</li>
              <li>Quand la recharge du véhicule s’est arrêtée</li>
              <li>Votre reçu final</li>
            </ul>

            <p>Vous pouvez également faire le suivi de votre session au moyen de l’application d’Electrify Canada.</p>
            <p>
              <em>
                Conseil : Vous recevrez uniquement des textos de la part d’’Electrify Canada sur le numéro de téléphone
                portable associé à chaque session de recharge active individuelle.*
              </em>
            </p>
            <p>
              <img loading="lazy" width="200" :src="require('@/assets/images/Faq/faq_fr.png')" alt="" />
            </p>
            <small>
              <p>
                <sup>*</sup>
                Des frais de messagerie et de données pourraient s’appliquer.
              </p>
            </small>
          </accordion-item>
          <accordion-item
            title="Que signifie « état de recharge »?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 1, loadInner: 15 } })"
          >
            <p>
              <router-link :to="{ name: 'glossary-fr', query: { load: 18 } }" class="link"
                >L’état de charge ou SOC</router-link
              >
              indique à quel point la batterie de votre VE est « pleine ». Lorsque la recharge commence, la plupart des
              VE envoient à la borne de recharge un SOC initial, qui est affiché sur l’écran de la borne de recharge.
              S’il est disponible, le SOC sera mis à jour au fur et à mesure que votre véhicule se recharge, bien que
              certains VE ne soient pas en mesure d’envoyer ces informations à nos bornes de recharge.
            </p>
            <p>
              Nos bornes de recharge afficheront également le temps nécessaire pour atteindre 80 % de SOC, si votre
              voiture transmet ces informations. Les VE ralentissent leur vitesse de recharge à mesure que la batterie
              approche d’une pleine recharge, ce qui se produit généralement autour de 80 %. Le SOC auquel cela se
              produit est souvent désigné sous le nom de « charge de masse. » Les véhicules continueront à se recharger
              jusqu'à 100 % à ce rythme plus lent pour préserver la durée de vie de votre batterie.
            </p>
            <p>
              <img loading="lazy" width="300" :src="require('@/assets/images/Faq/faqs_soc.jpg')" alt="" />
            </p>
          </accordion-item>
          <accordion-item
            title="Comment puis-je arrêter la session de recharge?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 1, loadInner: 16 } })"
          >
            <p>
              Pour terminer la session de charge, il suffit d’appuyer sur le bouton « Stop » (arrêt) dans le coin
              inférieur droit de l’écran de la borne de recharge. Vous pouvez également arrêter la session au moyen de
              l’application d’Electrify Canada. Sélectionnez « Arrêter la recharge » sur l’écran de la session actuelle.
            </p>
            <p>Conseil:</p>
            <ul>
              <li>
                Si vous ne voulez pas démarrer une session de recharge, mais que vous êtes déjà branché, vous devrez
                peut-être appuyer sur « Annuler (cancel) » sur l’écran de la borne de recharge pour libérer le
                connecteur de votre véhicule.
              </li>
              <li>
                Selon le modèle de votre véhicule, il se peut que vous deviez déverrouiller votre voiture pour retirer
                le connecteur.
              </li>
            </ul>
          </accordion-item>
          <accordion-item
            title="Que faire si j’obtiens une erreur de recharge?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 1, loadInner: 17 } })"
          >
            <p>
              L’écran peut vous demander de débrancher le connecteur de votre véhicule et de démarrer une nouvelle
              session en le rebranchant. Vous devez soumettre à nouveau le paiement pour commencer une nouvelle session.
              Si cela ne fonctionne toujours pas, veuillez vous déplacer vers une borne de recharge à proximité à cet
              emplacement si une borne de recharge est disponible. L’équipe d’assistance à la clientèle Electrify Canada
              est également là pour vous aider 24 heures sur 24, 7 jours sur 7. Composez le numéro sans frais
              1-833-231-3226.
            </p>
          </accordion-item>
          <accordion-item
            title="Pourquoi ma recharge ralentit-elle lorsque l’état de charge atteint 80 %?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 1, loadInner: 18 } })"
          >
            <p>
              Avant la fin de votre session, vous remarquerez peut-être que votre vitesse de recharge (kW) ralentit. Bon
              nombre de véhicules ralentissent la vitesse de recharge lorsque la charge atteint environ 80 %*.
            </p>
            <p>
              Pour cette raison, nous vous recommandons de débrancher votre véhicule une fois que vous avez atteint ce
              point. Au début d’une session de recharge, la plupart des VE partagent leur état de charge initial (EDC)
              avec la borne de recharge. L’EDC indique le niveau de charge de la batterie et peut être consulté sur
              l’écran de la borne de recharge ou dans l’application mobile Electrify Canada.
            </p>
            <p>
              Pour connaître l’EDC et d’autres renseignements concernant la recharge, téléchargez l’application
              Electrify Canada sur
              <a
                class="link"
                rel="noopener"
                target="_blank"
                href="https://apps.apple.com/ca/app/electrify-canada/id1472391910?l=fr"
              >
                App Store d’Apple
              </a>
              ou
              <a
                class="link"
                rel="noopener"
                target="_blank"
                href="https://play.google.com/store/apps/details?id=com.ec.evowner&hl=fr_CA"
              >
                Google Play</a
              >.
            </p>
            <p>
              * L’état de charge exact de la batterie en pourcentage peut varier. Pour savoir quand le point de
              ralentissement se produit sur votre véhicule, adressez-vous à son fabricant.
            </p>
          </accordion-item>
          <accordion-item
            title="Pourquoi facture-t-on 60 $ à la borne de recharge alors que je paie avec ma carte de crédit?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 1, loadInner: 19 } })"
          >
            <p>
              Le montant de 60 $ que vous voyez est une autorisation temporaire, et non une somme réellement facturée.
              Une fois la recharge terminée et la transaction réglée, la retenue de 60 $ est retirée et les frais
              facturés se limitent au coût de la transaction. Selon votre banque, la retenue est généralement retirée
              dans les 24 à 48 heures suivant le règlement de la transaction.
            </p>
          </accordion-item>
          <accordion-item
            title="Combien de temps prendra la recharge de mon VE à une borne Electrify Canada?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 1, loadInner: 20 } })"
          >
            <p>
              La durée de recharge varie pour chaque session et dépend de facteurs comme le type de VE, l’âge de la
              batterie et les conditions météorologiques. De nombreux VE modernes peuvent atteindre jusqu’à 80 % de
              recharge en 30 minutes environ en utilisant nos chargeurs ultrarapides. Vérifiez votre manuel du
              propriétaire ou les spécifications du véhicule en ligne pour trouver la capacité de votre batterie en
              kilowattheures (kWh). Souvenez-vous, plus le kW est élevé, plus la recharge est rapide.
            </p>
          </accordion-item>
          <accordion-item
            title="Puis-je utiliser la fonctionnalité Brancher et recharger avec mon VE à vos bornes?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 1, loadInner: 21 } })"
          >
            <p>
              La fonctionnalité Brancher et recharger est maintenant offert à toutes les bornes de recharge d’Electrify
              Canada. Toutefois, elle n’est offerte que pour les VE compatibles.
            </p>
            <p>
              Pour savoir si votre VE est compatible avec la fonctionnalité Brancher et recharger, veuillez communiquer
              avec le fabricant de votre véhicule ou consulter le manuel du propriétaire de votre véhicule pour en
              savoir plus.
            </p>
          </accordion-item>
          <accordion-item
            title="Puis-je acheter des actions d’Electrify Canada?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 1, loadInner: 22 } })"
          >
            <p>Non, Electrify Canada n’est pas une entreprise publique.</p>
          </accordion-item>
          <accordion-item
            title="Puis-je obtenir une carte RFID d’Electrify Canada?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 1, loadInner: 23 } })"
          >
            <p>
              Electrify Canada n’offre pas de cartes RFID pour le moment. Pour commencer une recharge, vous pouvez
              utiliser l’application mobile Electrify Canada, Apple Wallet, une carte de crédit ou la fonctionnalité
              Brancher et recharger pour les véhicules compatibles.
            </p>
          </accordion-item>
          <accordion-item
            title="Pourquoi certaines de vos bornes ont-elles deux câbles?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 1, loadInner: 24 } })"
          >
            <p>
              Les dernières générations de nos bornes de recharge sont dotées de deux câbles qui permettent d’accueillir
              différentes prises de recharge de VE afin de permettre l’accès facile à tous les véhicules.
            </p>
            <p>
              Nos bornes de recharge de nouvelle génération sont pourvues d’un câble de 24 pieds qui permet une
              compatibilité avec les différentes prises de recharge de VE.
            </p>
            <p>
              Veuillez noter que, quel que soit le modèle de la borne de recharge, elle ne peut recharger qu’un seul VE
              à la fois.
            </p>
          </accordion-item>
          <accordion-item
            title="Pourquoi mon VE prend-il plus de temps à se recharger lorsqu’il fait froid?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 1, loadInner: 25 } })"
          >
            <p>
              Par temps froid, le logiciel de votre VE abaisse généralement la puissance de recharge. Cette
              fonctionnalité est intégrée à votre véhicule pour protéger la batterie.
            </p>
            <p>
              Aussi est-il important, lorsque les températures sont plus froides, de prévoir des temps de recharge plus
              longs, que votre déplacement fasse partie de votre trajet quotidien ou d’un voyage en voiture plus long.
              Pour en savoir plus, lisez nos conseils de recharge par temps froid
              <a href="https://media.electrify-canada.ca/fr-ca/releases/30" target="_blank" class="link" rel="noopener"
                >ici</a
              >.
            </p>
          </accordion-item>
          <accordion-item
            title="Devrais-je stationner mon VE à l’extérieur par temps froid?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 1, loadInner: 26 } })"
          >
            <p>
              Oui, il est bon de stationner votre VE à l’extérieur durant les mois plus froids. Toutefois, en gardant
              votre VE dans un garage plus chaud, vous aidez la batterie à se recharger plus rapidement et à conserver
              sa charge plus longtemps.
            </p>
          </accordion-item>
          <accordion-item
            title="Des conseils pour charger un VE par temps froid?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 1, loadInner: 27 } })"
          >
            <img
              class="image--infograph"
              src="@/assets/images/Faq/faqs_cold_weather_tips_fr.webp"
              alt="1. Prévoyez des sessions de recharge plus longues 2. Les changements de température peuvent affecter l’autonomie du VE 3. Planifiez votre session de recharge 4. Stationnez votre VE à l’intérieur si possible 5. Utilisez les fonctionnalités pour temps froid de votre VE"
            />
          </accordion-item>
        </accordion>
      </accordion-item>
      <!-- Membership -->
      <accordion-item
        :headerBorder="true"
        title="Abonnement"
        :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 2 } }, true)"
      >
        <accordion>
          <accordion-item
            title="Pourquoi devrais-je devenir un membre d’Electrify Canada?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 2, loadInner: 0 } })"
            ><p>Les membres Pass et Pass+ d’Electrify Canada ont accès à l’application mobile qui leur permettent de</p>
            <ul>
              <li>voir la disponibilité en temps réel des bornes de recharge avant d’arriver à une station</li>
              <li>Ajoutez des stations favorites pour accélérer la recherche et la navigation</li>
              <li>Soyez averti lorsqu’une borne de recharge devient disponible</li>
              <li>
                définissez les notifications d’état de charge, de coût ou d’heure pour une session de recharge
                (uniquement pour les sessions avec la fonction « Balayer pour démarrer »)
              </li>
              <li>
                Commencez les sessions de recharge à l’aide d’un téléphone intelligent (plus besoin de chercher une
                carte de credit ainsi que les cartes de débit)
              </li>
              <li>Suivez l’état de recharge de votre véhicule si vous vous en éloignez</li>
              <li>Passez en revue l’historique de recharge et de facturation</li>
            </ul>
          </accordion-item>
          <accordion-item
            :title="$t('differencePassPassPlus')"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 2, loadInner: 1 } })"
          >
            <p>{{ $t('differencePassPassPlusAnswer1') }}</p>
            <ul>
              <li>{{ $t('differencePassPassPlusAnswer2') }}</li>
              <li>{{ $t('differencePassPassPlusAnswer3') }}</li>
            </ul>
            <p>{{ $t('differencePassPassPlusAnswer4') }}</p>
            <ul>
              <li>{{ $t('differencePassPassPlusAnswer5') }}</li>
            </ul>
          </accordion-item>
          <accordion-item
            title="Comment puis-je passer d’un type d’abonnement à l’autre?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 2, loadInner: 2 } })"
          >
            <p>
              Dans votre compte, allez dans Détails du plan (Plan details) et suivez les indications pour modifier les
              types de plans. Veuillez noter qu’une demande de changement d’abonnement de Passe+ à Passe n’entrera en
              vigueur que le 1er jour du prochain cycle de facturation, qui coïncide avec l’anniversaire mensuel de
              votre date d’inscription.
            </p>
          </accordion-item>
          <accordion-item
            title="Comment puis-je supprimer mon compte?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 2, loadInner: 3 } })"
          >
            <p>
              Annulez votre adhésion n’importe quand dans l’application en naviguant jusqu’à votre profil et en
              sélectionnant « Supprimer compte ».
            </p>
          </accordion-item>
        </accordion>
      </accordion-item>
      <!-- Pricing & Payment -->
      <accordion-item
        :headerBorder="true"
        title="Tarification et Paiement"
        :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 3 } }, true)"
      >
        <accordion>
          <HowToPay :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 3, loadInner: 0 } })"></HowToPay>
          <accordion-item
            :headerBorder="true"
            :title="$t('outOfMoney')"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 3, loadInner: 1 } })"
          >
            <p>
              {{ $t('outOfMoneyAnswer') }}
            </p>
          </accordion-item>
          <accordion-item
            :title="$t('determinePricing')"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 3, loadInner: 2 } })"
          >
            <p>
              {{ $t('determinePricingAnswer') }}
            </p>
          </accordion-item>
          <accordion-item
            :headerBorder="true"
            title="Comment la recharge automatique fonctionne-t-elle pendant ma première session de recharge?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 3, loadInner: 3 } })"
          >
            <p>
              Lorsque vous utilisez une borne Electrify Canada pour la première fois, le montant que vous aurez
              présélectionné dans notre application mobile au moment de la configuration initiale de votre compte vous
              sera facturé. Ce montant facturé est conservé dans votre compte pour couvrir tous les coûts
              supplémentaires qui ne sont pas inclus dans votre forfait, comme les frais d’occupation et les taxes
              applicables, ou pour de prochaines sessions de recharge. Pour que votre expérience de recharge soit
              harmonieuse, votre compte Electrify Canada sera automatiquement rechargé du montant que vous avez
              présélectionné dès qu’il passera sous le seuil de 5 $. Pour modifier le montant de votre recharge
              automatique, consultez l’onglet Compte dans l’application mobile Electrify Canada, où vous pouvez
              sélectionner un montant de 10 $, 20 $, 40 $ ou 60 $.
            </p>
          </accordion-item>
          <accordion-item
            title="Pourquoi dois-je fixer un montant de recharge automatique si mon offre Premium couvre mes coûts de recharge?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 3, loadInner: 4 } })"
          >
            <p>Votre offre Premium couvre la plupart des coûts de recharge, mais ne couvre pas :</p>
            <ul>
              <li>Les frais d’occupation</li>
              <li>Les limitations du temps de recharge de l’offre Premium</li>
            </ul>
            <p>
              Le montant de la recharge automatique vous permet de couvrir ces coûts tout en rechargeant sans
              interruption. Vérifiez les conditions d’utilisation de votre véhicule pour connaître les détails précis de
              votre offre Premium.
            </p>
          </accordion-item>
          <accordion-item
            title="Comment puis-je m’inscrire à mon offre Premium gratuite?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 3, loadInner: 5 } })"
          >
            <p>
              Le processus d’inscription varie selon le fabricant du véhicule. Certains fabricants de véhicules
              utilisent l’application Electrify Canada tandis que d’autres ont leur propre application mobile pour
              s’inscrire. Pour déterminer l’application nécessaire pour votre offre Premium, consultez notre
              <router-link class="link" :to="{ name: 'premium-offers-fr' }">
                page Web sur les offres Premium</router-link
              >
              où vous pouvez cliquer sur le fabricant de votre véhicule pour trouver des renseignements détaillés.
            </p>
            <p>
              Pour les fabricants de véhicules qui utilisent l’application Electrify Canada, l’offre Premium nécessite
              une inscription unique. Utilisez le code d’inscription fourni par votre concessionnaire pour obtenir votre
              recharge gratuite dans l’application Electrify Canada.
            </p>
            <ol>
              <li>
                Téléchargez l’application Electrify Canada<reference-link :num="1">
                  Les frais de messagerie et de données ainsi que les autres frais prélevés par votre fournisseur de
                  services cellulaires s’appliqueront à votre utilisation de l’application Electrify
                  Canada.</reference-link
                >
                et créez votre compte.
              </li>
              <li>À l’onglet Compte, sélectionnez <strong>Offre Premium</strong></li>
              <li>Appuyez sur <strong>J’ai un code d’inscription</strong></li>
              <li>Entrez votre code d’inscription et sélectionnez votre forfait.</li>
              <li>
                Acceptez la divulgation du forfait et ajoutez un mode de paiement de sauvegarde pour les frais qui ne
                sont pas couverts par votre recharge gratuite*
              </li>
            </ol>
            <p>
              * Pour que vous puissiez recharger votre véhicule en tout temps, le solde de votre compte Electrify Canada
              est automatiquement rechargé lorsqu’il descend sous le seuil de 5 $. Le montant de la recharge automatique
              que vous aurez choisi dans l’application vous sera facturé la première fois que vous rechargerez votre
              véhicule à une station Electrify Canada. Le montant facturé sera conservé dans votre compte pour couvrir
              les coûts non couverts par ce forfait, comme les frais d’occupation et les taxes applicables. Les
              utilisateurs qui ne débranchent pas et ne déplacent pas leur véhicule dans les dix minutes qui suivent la
              fin de leur session de recharge se voient facturer des frais d’occupation de 0,40 $ par minute. Ces frais
              visent à inciter les conducteurs à déplacer leur voiture rapidement afin que d’autres puissent utiliser la
              borne de recharge. Vous pouvez être informé de la fin de votre session de recharge par un message texte
              (les frais de messagerie et de données ainsi que les autres frais prélevés par votre fournisseur de
              services cellulaires pourraient s’appliquer) ou vous pouvez consulter l’écran de la borne de recharge.
            </p>
          </accordion-item>
        </accordion>
      </accordion-item>
      <!-- Mobile App -->
      <accordion-item
        :headerBorder="true"
        title="Appli mobile"
        :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 4 } }, true)"
      >
        <accordion>
          <accordion-item
            title="Comment puis-je accéder à mon compte Electrify Canada?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 4, loadInner: 0 } })"
          >
            <p>
              Vous aurez besoin de notre application mobile pour créer votre compte Electrify Canada et y ouvrir une
              session. Téléchargez l’application Electrify Canada sur
              <a
                class="link"
                rel="noopener"
                target="_blank"
                href="https://apps.apple.com/ca/app/electrify-canada/id1472391910?l=fr"
              >
                App Store d’Apple
              </a>
              ou
              <a
                class="link"
                rel="noopener"
                target="_blank"
                href="https://play.google.com/store/apps/details?id=com.ec.evowner&hl=fr_CA"
              >
                Google Play</a
              >. Une fois l’application téléchargée, vous pouvez vous inscrire ou vous connecter à votre compte.
            </p>
          </accordion-item>
          <accordion-item
            :title="$t('needNewAccount')"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 4, loadInner: 1 } })"
          >
            <p>{{ $t('needNewAccountAnswer') }}</p>
          </accordion-item>
          <accordion-item
            :title="$t('redesignedApp')"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 4, loadInner: 2 } })"
          >
            <p>
              {{ $t('redesignedAppAnswer') }}
            </p>
          </accordion-item>
          <accordion-item
            :title="$t('homestationWithApp')"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 4, loadInner: 3 } })"
          >
            <p>{{ $t('homestationWithAppAnswer') }}</p>
          </accordion-item>
          <accordion-item
            :title="$t('appToUseHomestation')"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 4, loadInner: 4 } })"
          >
            <p>
              {{ $t('appToUseHomestationAnswer') }}
            </p>
          </accordion-item>
          <accordion-item
            title="Qu’est-ce que l’authentification multifacteur et comment puis-je vérifier mon compte?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 4, loadInner: 5 } })"
          >
            <p>
              L’authentification multifacteur (AMF) ajoute une couche de sécurité supplémentaire pour protéger votre
              compte Electrify Canada. Lorsque vous vous connecterez, vous entrerez d’abord votre nom d’utilisateur et
              votre mot de passe, puis vous recevrez le message de vérifier votre identité à l’aide d’un code de
              vérification unique envoyé à votre courriel.
            </p>
            <p>
              Pour effectuer la vérification, entrez le code exactement comme il est indiqué sur l’écran « Vérifier
              votre identité » de l’application et sélectionnez « Continuer ». Ce processus vise à empêcher l’accès non
              autorisé à votre compte.
            </p>
            <p>Vous pouvez également suivre ces étapes lorsque vous vous connectez à l’application Electrify Canada:</p>
            <ol>
              <li>Ouvrez l’application Electrify Canada et connectez-vous à votre compte.</li>
              <li>
                Un code de vérification unique sera automatiquement envoyé à l’adresse de courriel associée à votre
                compte.
              </li>
              <li>
                Retournez à l’application Electrify Canada et entrez le code de vérification unique exactement comme il
                est indiqué sur l’écran « Vérifier votre identité ».
              </li>
              <li>Sélectionnez « Continuer » pour terminer le processus de vérification.</li>
              <li>Une fois celui-ci terminé, l’AMF sera activée dans votre compte pour renforcer la sécurité.</li>
            </ol>
          </accordion-item>
          <accordion-item
            title="Je ne peux pas accéder au courriel associé à mon compte. Comment puis-je effectuer l’authentification multifacteur?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 4, loadInner: 6 } })"
          >
            <p>
              Si vous ne parvenez pas à accéder au courriel associé à votre compte Electrify Canada, vous devrez
              demander la suppression du compte et créer un nouveau compte en l’associant à un courriel auquel vous
              pouvez accéder.
            </p>
          </accordion-item>
          <accordion-item
            title="J’ai reçu un courriel contenant un code d’authentification multifacteur pour vérifier mon compte, mais je n’ai pas tenté de me connecter. Que dois-je faire?"
            :onTrigger="() => updateURL({ name: 'faqs-fr', query: { loadOuter: 4, loadInner: 7 } })"
          >
            <p>
              Si vous avez reçu un code d’authentification multifacteur (AMF), mais que vous n’avez pas tenté de vous
              connecter, cela pourrait indiquer une tentative de connexion non autorisée. Pour des raisons de sécurité,
              nous vous recommandons de réinitialiser votre mot de passe immédiatement. Vous pouvez le faire au moyen de
              l’application ou du site Web d’Electrify Canada.
            </p>
          </accordion-item>
        </accordion>
      </accordion-item>
    </accordion>
  </article>
</template>

<script>
import lang from '@/lang';
import Faqs from '@/lang/Faqs';
import Accordion from '@/components/Accordion.vue';
import AccordionItem from '@/components/AccordionItem.vue';
import HowToPay from '@/components/Faqs/HowToPay.vue';
import ReferenceLink from '@/components/ReferenceLink.vue';
import HowToStartSession from '../../components/Faqs/HowToStartSession.vue';

export default {
  name: 'faqs',
  components: {
    AccordionItem,
    Accordion,
    HowToPay,
    HowToStartSession,
    ReferenceLink,
  },
  i18n: {
    messages: Faqs,
    sharedMessages: lang,
  },
  metaInfo: {
    title: 'Foire aux questions (FAQ) sur la recharge des VE',
    link: [
      {
        rel: 'canonical',
        href: 'https://www.electrify-canada.ca/fr/faqs/',
      },
    ],
    meta: [
      {
        name: 'description',
        content:
          'Consultez notre foire aux questions (FAQ) pour en savoir plus sur la recharge de votre véhicule électrique (VE) sur le réseau public de recharge d’Electrify Canada.',
      },
    ],
    script: [
      {
        type: 'application/ld+json',
        json: {
          '@context': 'http://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: ' Qu’est-ce qu’Electrify Canada? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Electrify Canada construit une infrastructure transformatrice de recharge ultrarapide en courant continu (CC) de véhicules électriques (VE) pour donner aux Canadiens la vitesse et la fiabilité dont ils ont besoin pour passer sans crainte à l’électrique.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' Est-ce que seuls les véhicules VW, Audi et Porsche peuvent utiliser electrify Canada? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Tout VE doté d’une entrée de recharge compatible CCS ou CHAdeMO peut utiliser Electrify Canada.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' Comment puis-je trouver une station de recharge Electrify Canada? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Pour trouver une station de recharge Electrify Canada près de vous, effectuez une recherche sur la carte en ligne ici. Vous pouvez également télécharger l’application Electrify Canada pour localiser une station ouverte, suivre les stations qui seront bientôt ouvertes et recevoir des notifications lorsqu’elles le seront.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'Pourquoi la borne de recharge affiche-t-elle un coût lorsque j’utilise mon offre Premium gratuite pour la recharge?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>La borne de recharge indique le prix initial de la recharge avant l’application des promotions et des taxes. À la fin de votre session, vous verrez votre prix lorsque l’offre Premium sera appliquée.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'Pourquoi certaines bornes de recharge disent-elles « temporairement réduit »?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Souvent, lorsque vous voyez une borne de recharge qui fournit une puissance réduite, cela signifie que notre équipe d’entretien a temporairement réduit la puissance de la borne pour y faire des améliorations et des mises à jour. Bien que ces bornes de recharge fonctionnent toujours, elles fonctionnent à des niveaux de puissance réduits. Nous comprenons les inconvénients d’une vitesse de recharge plus lente et nous travaillons toujours à améliorer votre expérience afin d’accélérer les recharges.</p><p>Veuillez noter que les sites ne sont pas tous touchés. Pour voir d’avance le niveau de puissance desbornes à une station, utilisez l’application mobile Electrify Canada.<p><p>Merci pour votre patience et votre soutien.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'Comment puis-je accéder à mon compte Electrify Canada?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Vous aurez besoin de notre application mobile pour créer votre compte Electrify Canada et y ouvrir une session. Téléchargez l’application Electrify Canada sur <a class="link" rel="noopener" target="_blank" href="https://apps.apple.com/ca/app/electrify-canada/id1472391910?l=fr">App Store d’Apple</a> ou <a class="link" rel="noopener" target="_blank" href="https://play.google.com/store/apps/details?id=com.ec.evowner&hl=fr_CA">Google Play</a>. Une fois l’application téléchargée, vous pouvez vous inscrire ou vous connecter à votre compte.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'Pourquoi ma recharge ralentit-elle lorsque l’état de charge atteint 80 %?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Avant la fin de votre session, vous remarquerez peut-être que votre vitesse de recharge (kW) ralentit. Bon nombre de véhicules ralentissent la vitesse de recharge lorsque la charge atteint environ 80 %*.</p><p>Pour cette raison, nous vous recommandons de débrancher votre véhicule une fois que vous avez atteint ce point. Au début d’une session de recharge, la plupart des VE partagent leur état de charge initial (EDC) avec la borne de recharge. L’EDC indique le niveau de charge de la batterie et peut être consulté sur l’écran de la borne de recharge ou dans l’application mobile Electrify Canada.</p><p>Pour connaître l’EDC et d’autres renseignements concernant la recharge, téléchargez l’application Electrify Canada sur <a class="link" rel="noopener" target="_blank" href="https://apps.apple.com/ca/app/electrify-canada/id1472391910?l=fr">App Store d’Apple</a> ou <a class="link" rel="noopener" target="_blank" href="https://play.google.com/store/apps/details?id=com.ec.evowner&hl=fr_CA">Google Play</a>.</p><p>* L’état de charge exact de la batterie en pourcentage peut varier. Pour savoir quand le point de ralentissement se produit sur votre véhicule, adressez-vous à son fabricant.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'Pourquoi facture-t-on 60 $ à la borne de recharge alors que je paie avec ma carte de crédit?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Le montant de 60 $ que vous voyez est une autorisation temporaire, et non une somme réellement facturée. Une fois la recharge terminée et la transaction réglée, la retenue de 60 $ est retirée et les frais facturés se limitent au coût de la transaction. Selon votre banque, la retenue est généralement retirée dans les 24 à 48 heures suivant le règlement de la transaction.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'Combien de temps prendra la recharge de mon VE à une borne Electrify Canada?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>La durée de recharge varie pour chaque session et dépend de facteurs comme le type de VE, l’âge de la batterie et les conditions météorologiques. De nombreux VE modernes peuvent atteindre jusqu’à 80 % de recharge en 30 minutes environ en utilisant nos chargeurs ultrarapides. Vérifiez votre manuel du propriétaire ou les spécifications du véhicule en ligne pour trouver la capacité de votre batterie en kilowattheures (kWh). Souvenez-vous, plus le kW est élevé, plus la recharge est rapide.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'Puis-je utiliser la fonctionnalité Brancher et recharger avec mon VE à vos bornes?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>La fonctionnalité Brancher et recharger est maintenant offerte à toutes les bornes de recharge d’Electrify Canada. Toutefois, elle n’est offerte que pour les VE compatibles.</p><p>Pour savoir si votre VE est compatible avec la fonctionnalité Brancher et recharger, veuillez communiquer avec le fabricant de votre véhicule ou consulter le manuel du propriétaire de votre véhicule pour en savoir plus.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'Puis-je acheter des actions d’Electrify Canada?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Non, Electrify Canada n’est pas une entreprise publique.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'Puis-je obtenir une carte RFID d’Electrify Canada?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Electrify Canada n’offre pas de cartes RFID pour le moment. Pour commencer une recharge, vous pouvez utiliser l’application mobile Electrify Canada, Apple Wallet, une carte de crédit ou la fonctionnalité Brancher et recharger pour les véhicules compatibles.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'Pourquoi certaines de vos bornes ont-elles deux câbles?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Les dernières générations de nos bornes de recharge sont dotées de deux câbles qui permettent d’accueillir différentes prises de recharge de VE afin de permettre l’accès facile à tous les véhicules.</p><p>Nos bornes de recharge de nouvelle génération sont pourvues d’un câble de 24 pieds qui permet une compatibilité avec les différentes prises de recharge de VE.</p><p>Veuillez noter que, quel que soit le modèle de la borne de recharge, elle ne peut recharger qu’un seul VE à la fois.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'Pourquoi mon VE prend-il plus de temps à se recharger lorsqu’il fait froid?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Par temps froid, le logiciel de votre VE abaisse généralement la puissance de recharge. Cette fonctionnalité est intégrée à votre véhicule pour protéger la batterie.</p><p>Aussi est-il important, lorsque les températures sont plus froides, de prévoir des temps de recharge plus longs, que votre déplacement fasse partie de votre trajet quotidien ou d’un voyage en voiture plus long. Pour en savoir plus, lisez nos conseils de recharge par temps froid <a href="https://media.electrify-canada.ca/fr-ca/releases/30" target="_blank" class="link" rel="noopener">ici</a>.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'Devrais-je stationner mon VE à l’extérieur par temps froid?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Oui, il est bon de stationner votre VE à l’extérieur durant les mois plus froids. Toutefois, en gardant votre VE dans un garage plus chaud, vous aidez la batterie à se recharger plus rapidement et à conserver sa charge plus longtemps.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'Puis-je utiliser une carte d’adhésion ou l’application d’un autre réseau de recharge aux bornes d’Electrify Canada?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Aucune carte d’adhésion ou application d’un autre réseau de recharge ne peut être utilisée aux bornes d’Electrify Canada.</p>',
              },
            },

            {
              '@type': 'Question',
              name: 'Que dois-je faire si une erreur de recharge se produit?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Dans bien des cas, les erreurs de recharge sont temporaires et peuvent être résolues en arrêtant et en redémarrant votre session. Voici ce qu’il faut faire :</p><ol><li>Mettez fin à votre session de recharge manuellement en appuyant sur « Arrêter » dans l’application mobile ou sur la borne de recharge.</li><li>Débranchez le câble de recharge de votre véhicule. Si nécessaire, déverrouillez votre véhicule électrique avant de débrancher le câble de recharge.</li><li>Rebranchez le câble de recharge et commencez une nouvelle session de recharge.</li><li>Si l’erreur persiste, nous vous suggérons de vous déplacer vers une autre borne de recharge et de signaler le problème en composant le numéro du centre d’assistance à la clientèle situé au-dessus de l’écran de la borne de recharge.</li><li>Pour obtenir des instructions plus détaillées sur la façon de recharger, consultez <router-link class="link" :name="{ to: \'charging-your-ev-fr\' }">Comment recharger votre VE</router-link></li></ol>',
              },
            },
            {
              '@type': 'Question',
              name: 'Ai-je besoin d’une adhésion Passe ou Passe+ Electrify Canada pour recharger?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Non, il n’est pas nécessaire d’adhérer à un forfait d’Electrify Canada, mais l’adhésion comporte des avantages. Par exemple, les membres Passe+ économisent 20 % chaque fois qu’ils rechargent. Pour en savoir plus sur les avantages d’adhérer à un forfait Passe ou Passe+, <router-link class="link" :to="{ name: \'mobile-app-fr\' }">cliquez ici</router-link>.</p><p>Si vous avez besoin d’un soutien supplémentaire ou si vous devez commencer une session de recharge par téléphone, un numéro sans frais est indiqué sur chaque borne de recharge.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'Comment la recharge automatique fonctionne-t-elle pendant ma première session de recharge?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Lorsque vous utilisez une borne Electrify Canada pour la première fois, le montant que vous aurez présélectionné dans notre application mobile au moment de la configuration initiale de votre compte vous sera facturé. Ce montant facturé est conservé dans votre compte pour couvrir tous les coûts supplémentaires qui ne sont pas inclus dans votre forfait, comme les frais d’occupation et les taxes applicables, ou pour de prochaines sessions de recharge. Pour que votre expérience de recharge soit harmonieuse, votre compte Electrify Canada sera automatiquement rechargé du montant que vous avez présélectionné dès qu’il passera sous le seuil de 5 $. Pour modifier le montant de votre recharge automatique, consultez l’onglet Compte dans l’application mobile Electrify Canada, où vous pouvez sélectionner un montant de 10 $, 20 $, 40 $ ou 60 $.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'Pourquoi dois-je fixer un montant de recharge automatique si mon offre Premium couvre mes coûts de recharge?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Votre offre Premium couvre la plupart des coûts de recharge, mais ne couvre pas :</p><ul><li>Les frais d’occupation</li><li>Les limitations du temps de recharge de l’offre Premium</li></ul><p>Le montant de la recharge automatique vous permet de couvrir ces coûts tout en rechargeant sans interruption. Vérifiez les conditions d’utilisation de votre véhicule pour connaître les détails précis de votre offre Premium.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'Comment puis-je m’inscrire à mon offre Premium gratuite?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Le processus d’inscription varie selon le fabricant du véhicule. Certains fabricants de véhicules utilisent l’application Electrify Canada tandis que d’autres ont leur propre application mobile pour s’inscrire. Pour déterminer l’application nécessaire pour votre offre Premium, consultez notre <router-link class="link" :to="{ name: \'premium-offers-fr\' }">page Web sur les offres Premium</router-link> où vous pouvez cliquer sur le fabricant de votre véhicule pour trouver des renseignements détaillés.</p><p>Pour les fabricants de véhicules qui utilisent l’application Electrify Canada, l’offre Premium nécessite une inscription unique. Utilisez le code d’inscription fourni par votre concessionnaire pour obtenir votre recharge gratuite dans l’application Electrify Canada.</p><ol><li>Téléchargez l’application Electrify Canada<reference-link :num="1">Les frais de messagerie et de données ainsi que les autres frais prélevés par votre fournisseur de services cellulaires s’appliqueront à votre utilisation de l’application Electrify Canada.</reference-link> et créez votre compte.</li><li>À l’onglet Compte, sélectionnez <strong>Offre Premium</strong></li><li>Appuyez sur <strong>J’ai un code d’inscription</strong></li><li>Entrez votre code d’inscription et sélectionnez votre forfait.</li><li>Acceptez la divulgation du forfait et ajoutez un mode de paiement de sauvegarde pour les frais qui ne sont pas couverts par votre recharge gratuite*</li></ol><p>* Pour que vous puissiez recharger votre véhicule en tout temps, le solde de votre compte Electrify Canada est automatiquement rechargé lorsqu’il descend sous le seuil de 5 $. Le montant de la recharge automatique que vous aurez choisi dans l’application vous sera facturé la première fois que vous rechargerez votre véhicule à une station Electrify Canada. Le montant facturé sera conservé dans votre compte pour couvrir les coûts non couverts par ce forfait, comme les frais d’occupation et les taxes applicables. Les utilisateurs qui ne débranchent pas et ne déplacent pas leur véhicule dans les dix minutes qui suivent la fin de leur session de recharge se voient facturer des frais d’occupation de 0,40 $ par minute. Ces frais visent à inciter les conducteurs à déplacer leur voiture rapidement afin que d’autres puissent utiliser la borne de recharge. Vous pouvez être informé de la fin de votre session de recharge par un message texte (les frais de messagerie et de données ainsi que les autres frais prélevés par votre fournisseur de services cellulaires pourraient s’appliquer) ou vous pouvez consulter l’écran de la borne de recharge.</p>',
              },
            },

            {
              '@type': 'Question',
              name: ' Si je ne suis pas membre, comment puis-je commencer une session de recharge à une borne de recharge Electrify Canada? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>En tant qu’invité (sans compte Electrify Canada), vous pouvez payer par carte de crédit. Chaque borne Electrify Canada est équipé d’un lecteur de carte de crédit afin de rendre la recharge facilement accessible à tous les utilisateurs d’un VE.</p><p>Branchez d’abord le connecteur au véhicule. Suivez toutes les étapes sur l’écran tactile avant de saisir une carte de crédit Visa ou Mastercard pour le paiement. Si votre carte est munie d’une puce, veuillez l’insérer complètement dans la partie inférieure du lecteur.</p><p>Une autorisation temporaire de 60 $ sera appliquée à toutes les cartes de crédit ou de débit, et elle sera libérée après la facturation La durée de cette retenue est déterminée par votre fournisseur de carte.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' Que signifient les étiquettes sur les bornes Electrify Canada? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Nous utilisons des étiquettes de la borne créées en collaboration avec des conducteurs comme vous! Dans le haut de chaque étiquette, vous trouverez des icônes de connecteur qui correspondent au port de recharge de votre véhicule. Dans le bas, des icônes représentant un éclair indiquent la puissance de la borne : plus la valeur de kilowatt (kW) est élevée, plus la puissance de recharge est élevée.</p><p>Nos stations de recharge proposent trois types de connecteurs :</p><p>Les étiquettes vertes indiquent les connecteurs SRC hyper rapides procurant jusqu’à 350 kW.</p><p>Les étiquettes turquoise indiquent les connecteurs SRC ultrarapides procurant jusqu’à 150 kW.</p><p>Les étiquettes bleues indiquent les connecteurs de recharge « CHAdeMO » procurant jusqu’à 50 kW.</p><p>Conseil : Ce ne sont pas tous les véhicules électriques qui prennent en charge le niveau de puissance maximal d’un connecteur. Par exemple, une étiquette « hyper rapide » signifie que la borne offre jusqu’à 350 kW pour un VE compatible avec les connecteurs SRC. Si votre véhicule ne peut pas recevoir une charge maximale de 350 kW, alors la borne lui fournit automatiquement la puissance la plus élevée qu’il peut prendre en charge. Pour connaître les capacités de recharge de votre véhicule, consultez le manuel du fabricant. Si vous avez besoin d’assistance à la station, communiquez avec notre équipe de service à la clientèle. Vous pouvez nous joindre 24 heures sur 24, 7 jours sur 7 en composant le numéro sans frais 1 833 231-3226.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' Si un connecteur sur une borne de recharge est déjà branché sur un véhicule, puis-je utiliser l’autre? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Un seul connecteur par borne de recharge rapide CC d’Electrify Canada peut être utilisé en même temps. Si l’un ou l’autre des connecteurs est en cours d’utilisation, veuillez chercher la borne de recharge disponible suivante sur ce site. Chacune de nos bornes de recharge est dotée de deux connecteurs adaptés aux véhicules avec un emplacement de port de recharge différent.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' À quelle vitesse puis-je recharger? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Chaque emplacement offre des vitesses de chargement pouvant atteindre 150 kW ou 350 kW pour les véhicules compatibles. En fonction d’un certain nombre de facteurs, surtout la capacité du véhicule, les vitesses de recharge peuvent fournir une autonomie de 4 à 30 km pour chaque minute de recharge.1</p><p>1 Chiffres basés sur des estimations internes et susceptibles de changer. L’autonomie réelle varie en fonction des éléments suivants, mais sans s’y limiter : type de véhicule; habitudes de conduite et de recharge; météo et température; âge de la batterie et état du véhicule. Veuillez noter que la capacité de la batterie diminue avec le temps et l’utilisation.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' Quels modes de paiement sont acceptés? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Toutes les bornes de recharge d’Electrify Canada acceptent les cartes de crédit Visa et MasterCard, ainsi que les cartes de débit.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' Que sont les frais d’inactivité? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Les conducteurs qui ne débranchent pas et ne déplacent pas leur véhicule dix minutes après la fin de leur session de recharge seront facturés des frais d’inactivité de 0,40 $ par minute. Ces frais encouragent les conducteurs à déplacer leur voiture rapidement afin que d’autres puissent utiliser la borne de recharge. Vous pouvez être averti lorsque votre session de recharge est terminée par texto2 ou le voir sur l’écran de la borne de recharge.</p><p>2 Des frais de messagerie et de données peuvent s’appliquer.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' Comment obtenir un reçu? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Si vous avez une adhésion, ouvrez une session dans l’application Electrify Canada, allez à votre compte et sélectionnez « Historique de recharge ». Vous pouvez filtrer votre historique par date et le partager par courriel. Vous pouvez aussi recevoir votre reçu par message texte si vous indiquez votre numéro de téléphone cellulaire au début de la session. (Des frais de messagerie et de données pourraient s’appliquer.) Le coût total sera également affiché sur l’écran de la station de recharge à la fin de votre session.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' Comment puis-je suivre la progression de ma session de recharge? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Si vous fournissez votre numéro de mobile au début de votre session, vous pouvez recevoir des mises à jour par texto pour voir*:</p><p>Quand la recharge du véhicule a commencé</p><p>Quand la recharge du véhicule s’est ralentie</p><p>Quand la recharge du véhicule s’est arrêtée</p><p>Votre reçu final</p><p>Vous pouvez également faire le suivi de votre session au moyen de l’application d’Electrify Canada.</p><p>Conseil : Vous recevrez uniquement des textos de la part d’’Electrify Canada sur le numéro de téléphone portable associé à chaque session de recharge active individuelle.*</p><p>* Des frais de messagerie et de données pourraient s’appliquer.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' Que signifie « état de recharge »? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>L’état de charge ou SOC indique à quel point la batterie de votre VE est « pleine ». Lorsque la recharge commence, la plupart des VE envoient à la borne de recharge un SOC initial, qui est affiché sur l’écran de la borne de recharge. S’il est disponible, le SOC sera mis à jour au fur et à mesure que votre véhicule se recharge, bien que certains VE ne soient pas en mesure d’envoyer ces informations à nos bornes de recharge.</p><p>Nos bornes de recharge afficheront également le temps nécessaire pour atteindre 80 % de SOC, si votre voiture transmet ces informations. Les VE ralentissent leur vitesse de recharge à mesure que la batterie approche d’une pleine recharge, ce qui se produit généralement autour de 80 %. Le SOC auquel cela se produit est souvent désigné sous le nom de « charge de masse. » Les véhicules continueront à se recharger jusqu&#39;à 100 % à ce rythme plus lent pour préserver la durée de vie de votre batterie.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' Comment puis-je arrêter la session de recharge? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Pour terminer la session de charge, il suffit d’appuyer sur le bouton « Stop » (arrêt) dans le coin inférieur droit de l’écran de la borne de recharge. Vous pouvez également arrêter la session au moyen de l’application d’Electrify Canada. Sélectionnez « Arrêter la recharge » sur l’écran de la session actuelle.</p><p>Conseil:</p><p>Si vous ne voulez pas démarrer une session de recharge, mais que vous êtes déjà branché, vous devrez peut-être appuyer sur « Annuler (cancel) » sur l’écran de la borne de recharge pour libérer le connecteur de votre véhicule.</p><p>Selon le modèle de votre véhicule, il se peut que vous deviez déverrouiller votre voiture pour retirer le connecteur.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' Que faire si j’obtiens une erreur de recharge? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>L’écran peut vous demander de débrancher le connecteur de votre véhicule et de démarrer une nouvelle session en le rebranchant. Vous devez soumettre à nouveau le paiement pour commencer une nouvelle session. Si cela ne fonctionne toujours pas, veuillez vous déplacer vers une borne de recharge à proximité à cet emplacement si une borne de recharge est disponible. L’équipe d’assistance à la clientèle Electrify Canada est également là pour vous aider 24 heures sur 24, 7 jours sur 7. Composez le numéro sans frais 1-833-231-3226.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' La recharge ralentit vers la fin de ma session. Est-ce normal? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Oui. La vitesse de recharge diminue vers la fin de votre session, peu importe la marque et le modèle de votre VE. Pour prolonger la durée de vie de la batterie, beaucoup de véhicules ralentissent la vitesse de recharge quand ils atteignent environ 80 % de la capacité ou de l’état de charge (EDC), même si le pourcentage exact peut varier pour votre véhicule.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' Pourquoi devrais-je devenir un membre d’Electrify Canada? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Les membres Pass et Pass+ d’Electrify Canada ont accès à l’application mobile qui leur permettent de</p>\n<p>voir la disponibilité en temps réel des bornes de recharge avant d’arriver à une station</p>\n<p>Ajoutez des stations favorites pour accélérer la recherche et la navigation</p>\n<p>Soyez averti lorsqu’une borne de recharge devient disponible</p>\n<p>définissez les notifications d’état de charge, de coût ou d’heure pour une session de recharge (uniquement pour les sessions avec la fonction « Balayer pour démarrer »)</p>\n<p>Commencez les sessions de recharge à l’aide d’un téléphone intelligent (plus besoin de chercher une carte de credit ainsi que les cartes de débit)</p>\n<p>Suivez l’état de recharge de votre véhicule si vous vous en éloignez</p>\n<p>Passez en revue l’historique de recharge et de facturation</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' Quelles sont les différences entre les abonnements Passe et Passe+? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: `<p>Les membres Passe d’Electrify Canada bénéficient de tous les avantages ci-dessus et</p><p>Aucun frais à l’inscription et aucuns frais mensuels</p><p>Bénéficiez d’un accès pratique à la recharge via votre téléphone intelligent</p><p>Les membres Passe+ d’Electrify Canada bénéficient de tous les avantages ci-dessus et</p><p>Payez des frais mensuels pour profiter d'économies allant jusqu'à 20 % sur la recharge.</p>`,
              },
            },
            {
              '@type': 'Question',
              name: ' Comment puis-je passer d’un type d’abonnement à l’autre? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Dans votre compte, allez dans Détails du plan (Plan details) et suivez les indications pour modifier les types de plans. Veuillez noter qu’une demande de changement d’abonnement de Passe+ à Passe n’entrera en vigueur que le 1er jour du prochain cycle de facturation, qui coïncide avec l’anniversaire mensuel de votre date d’inscription.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' Comment puis-je supprimer mon compte? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Annulez votre adhésion n’importe quand dans l’application en naviguant jusqu’à votre profil et en sélectionnant « Supprimer compte ».</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' Comment puis-je payer les recharges et les frais d’adhésion du Passe+? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Lorsque vous adhérez à un forfait Passe ou Passe+ d’Electrify Canada dans l’application mobile, suivez les messages pour ajouter votre carte de crédit à votre compte. Sélectionnez ensuite un montant à ajouter automatiquement à votre solde Electrify Canada.</p><p>Ce montant sera ajouté à votre compte la première fois que vous effectuerez une recharge à une station Electrify Canada après votre inscription. Chaque fois que votre solde atteindra 5 $ ou moins, le montant que vous avez sélectionné sera ajouté à votre compte.</p><p>Les frais de recharge, les frais d’occupation et toutes les taxes applicables seront déduits de votre solde à la fin de la session de recharge.</p><p>Si vous êtes un membre Passe+, vos frais d’adhésion mensuels (plus les frais et les taxes applicables) seront portés à votre carte de crédit ou de débit au moment de votre inscription puis déduits de votre solde Electrify Canada tous les mois par la suite.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' Que se passe-t-il si je manque d’argent dans mon portefeuille d’application mobile d’Electrify Canada? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Pour que vous puissiez recharger votre véhicule en toute transparence à une borne de recharge Electrify Canada , chaque fois que le solde de votre portefeuille tombe à ou en dessous du seuil minimum du portefeuille, nous débiterons automatiquement votre carte au dossier pour remplir automatiquement votre portefeuille avec le montant que vous sélectionnez. Si votre mode de paiement est refusé au cours d’une session de recharge, veuillez mettre à jour votre mode de paiement sur votre compte Electrify Canada pour permettre le remplissage automatique de votre portefeuille afin que la recharge du véhicule puisse continuer. Si le mode de paiement n’est pas mis à jour, la recharge s’arrêtera lorsque le solde de votre portefeuille atteindra 0 $.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' Comment la tarification de la recharge rapide CC d’Electrify Canada est-elle déterminée? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Vous payez pour l’énergie que votre véhicule reçoit, quelle que soit la température, la capacité de votre batterie, le temps requis pour la recharge de votre véhicule ou d’autres facteurs variables.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' Dois-je créer un nouveau compte pour utiliser la nouvelle application? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Non, vous pouvez utiliser vos identifiants de compte Electrify Canada actuels pour ouvrir une session dans la nouvelle application.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' De quelle façon l’application redessinée facilite-t-elle la recharge à la maison? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>L’application vous permettra de vous connecter à la borne domestique HomeStation™ Electrify. Vous pourrez démarrer et arrêter une session de recharge et établir un calendrier de recharge pour votre VE.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' La borne domestique HomeStation™ est-elle compatible avec l’appli Electrify Canada? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Oui. L’application Electrify Canada est offerte pour iOS et Android.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' Ai-je besoin de l’appli Electrify Canada pour utiliser la borne domestique HomeStation™? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Non, l’appli Electrify Canada n’est pas nécessaire pour utiliser votre borne domestique HomeStation™. Cependant, l’appli est nécessaire pour profiter de toutes les fonctionnalités de HomeStation™.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'Qu’est-ce que l’authentification multifacteur et comment puis-je vérifier mon compte?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>L’authentification multifacteur (AMF) ajoute une couche de sécurité supplémentaire pour protéger votre compte Electrify Canada. Lorsque vous vous connecterez, vous entrerez d’abord votre nom d’utilisateur et votre mot de passe, puis vous recevrez le message de vérifier votre identité à l’aide d’un code de vérification unique envoyé à votre courriel. </p> <p> Pour effectuer la vérification, entrez le code exactement comme il est indiqué sur l’écran « Vérifier votre identité » de l’application et sélectionnez « Continuer ». Ce processus vise à empêcher l’accès non autorisé à votre compte. </p> <p>Vous pouvez également suivre ces étapes lorsque vous vous connectez à l’application Electrify Canada:</p> <ol> <li>Ouvrez l’application Electrify Canada et connectez-vous à votre compte.</li> <li> Un code de vérification unique sera automatiquement envoyé à l’adresse de courriel associée à votre compte. </li> <li> Retournez à l’application Electrify Canada et entrez le code de vérification unique exactement comme il est indiqué sur l’écran « Vérifier votre identité ». </li> <li>Sélectionnez « Continuer » pour terminer le processus de vérification.</li> <li>Une fois celui-ci terminé, l’AMF sera activée dans votre compte pour renforcer la sécurité.</li> </ol>',
              },
            },
            {
              '@type': 'Question',
              name: 'Je ne peux pas accéder au courriel associé à mon compte. Comment puis-je effectuer l’authentification multifacteur?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p> Si vous ne parvenez pas à accéder au courriel associé à votre compte Electrify Canada, vous devrez demander la suppression du compte et créer un nouveau compte en l’associant à un courriel auquel vous pouvez accéder.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'J’ai reçu un courriel contenant un code d’authentification multifacteur pour vérifier mon compte, mais je n’ai pas tenté de me connecter. Que dois-je faire?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Si vous avez reçu un code d’authentification multifacteur (AMF), mais que vous n’avez pas tenté de vous connecter, cela pourrait indiquer une tentative de connexion non autorisée. Pour des raisons de sécurité, nous vous recommandons de réinitialiser votre mot de passe immédiatement. Vous pouvez le faire au moyen de l’application ou du site Web d’Electrify Canada.</p>',
              },
            },
          ],
        },
      },
    ],
  },
  computed: {
    loadOuter() {
      return this.$route.query.loadOuter;
    },
    loadInner() {
      return this.$route.query.loadInner;
    },
  },
  methods: {
    updateURL(data, isParent = false) {
      const routeParams = this.$route.query;
      const dataParams = data.query;

      const changeInParams =
        parseInt(routeParams.loadInner, 10) !== parseInt(dataParams.loadInner, 10) ||
        parseInt(routeParams.loadOuter, 10) !== parseInt(dataParams.loadOuter, 10);

      const changeInOuterParams = parseInt(routeParams.loadOuter, 10) !== parseInt(dataParams.loadOuter, 10);

      if ((isParent && changeInOuterParams) || changeInParams) {
        this.$router.replace(data);
      }
    },
    scrollToQuestion() {
      // PARENT ACCORDION
      const parentAccordion = document.querySelector('.accordion');
      const parentAccordionId = parentAccordion.id.split('-')[0];
      // Get all the items in the parent accordion by filtering out nested accordion content
      const parentItems = Array.from(parentAccordion?.querySelectorAll('[class~=accordion-trigger]')).filter(
        (item) => !item.closest('.accordion-content')
      );

      if (this.loadOuter >= 0 && this.loadInner >= 0) {
        const targetParentItem = parentItems[this.loadOuter];
        const targetParentItemId = targetParentItem?.id.split('-')[0];

        // CHILD ACCORDION
        const childAccordion = targetParentItem?.nextElementSibling.querySelector('.accordion');
        const childAccordionId = childAccordion?.id.split('-')[0];

        const childItems = childAccordion?.querySelectorAll('[class~=accordion-trigger]');
        const targetChildItem = childItems[this.loadInner];
        const targetChildItemId = targetChildItem?.id.split('-')[0];

        // Trigger parent and child accordion items to open
        this.$root.$emit('toggleAccordion', {
          accordionID: parentAccordionId,
          uniqueID: targetParentItemId,
        });

        this.$root.$emit('toggleAccordion', {
          accordionID: childAccordionId,
          uniqueID: targetChildItemId,
        });

        // Scroll open item into view
        setTimeout(() => {
          const el = document.getElementById(targetChildItem?.id);
          el?.scrollIntoView({ behavior: 'smooth' });
        }, 500);
      } else if (this.loadOuter >= 0) {
        const targetParentItem = parentItems[this.loadOuter];
        const targetParentItemId = targetParentItem?.id.split('-')[0];
        // Trigger parent and child accordion items to open
        this.$root.$emit('toggleAccordion', {
          accordionID: parentAccordionId,
          uniqueID: targetParentItemId,
        });

        // Scroll open item into view
        setTimeout(() => {
          const el = document.getElementById(targetParentItem?.id);
          el?.scrollIntoView({ behavior: 'smooth' });
        }, 500);
      }
    },
  },
  mounted() {
    this.scrollToQuestion();
  },
  watch: {
    $route() {
      // Prevents the page from scrolling to the top when the query parameters in the URL get updated (default behaviour of Vue router on route change)
      this.scrollToQuestion();
    },
  },
};
</script>
